import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

import {
  Container,
  Table,
  Modal,
  Row,
  Col,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { STUDENT_INTERNSHIP_API } from "../../../api/api";

// import { FaEye } from "react-icons/fa";
// import { RiDownload2Fill } from "react-icons/ri";

import InnerPage from "../../../assets/student/inner-page-image.png";

import view from "../../../assets/icons/student/view-eye.svg";
import download from "../../../assets/icons/student/download.svg";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentInternship.css";

const StudentInternship = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    projectName: "",
    githubUrl: "",
    liveUrl: "",
    description: "",
    appliedDate: "",
    startDate: "",
    endDate: "",
    role: "",
    certificate: "",
  });
  const [status, setStatus] = useState(null);

  const [showCertificateModal, setShowCertificateModal] = useState(false);

  //loading
  const [loadingInternship, setloadingInternship] = useState(true);
  const [isInternshipLoading, setIsInternshipLoading] = useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const fetchInternshipData = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setloadingInternship(false);
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        STUDENT_INTERNSHIP_API.GET_INTERNSHIP,
        { user_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200 && data.data) {
        const user = data.data;

        setFormData({
          fullName: user.full_name || "",
          email: user.email || "",
          phoneNumber: user.phone_number || "",
          projectName: user.project_name || "",
          githubUrl: user.github_url || "",
          liveUrl: user.web_url || "",
          description: user.description || "",
          appliedDate: user.applied_date,
          startDate: user.start_date,
          endDate: user.end_date,
          role: user.role || "",
          certificate: user.intership_certificate || "",
        });

        setStatus(user.status);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching intership:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setloadingInternship(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchInternshipData();
  }, [fetchInternshipData]);

  const handleInternshipSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!token || !userId) {
      setIsInternshipLoading(false);
      navigate("/signin");

      return;
    }

    // Field validation
    for (const key of [
      // "projectName",
      "phoneNumber",
      // "githubUrl",
      // "liveUrl",
      // "description",
    ]) {
      const value = formData[key];

      if (value == null || value.trim() === "") {
        showErrorToast(`Please fill ${key.replace(/([A-Z])/g, " $1")}`);
        return;
      }
    }

    // Project Name only letters & spaces
    if (!/^[A-Za-z\s .,'()&/-]+$/.test(formData.projectName)) {
      showErrorToast("Project Name: alphabets only");
      return;
    }

    // Phone number: exactly 10 digits
    if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
      showErrorToast("Enter a valid 10-digit phone number.");
      return;
    }

    // Description cannot be all numbers
    if (!/[a-zA-Z .,'()&]/.test(formData.description)) {
      showErrorToast("Description cannot be numbers only");
      return;
    }

    if (formData.description.length < 30) {
      showErrorToast("Description must be at least 30 characters.");
      return;
    }

    const internshipPayload = {
      student_id: userId,
      project_name: formData.projectName,
      // phone_number: formData.phoneNumber,
      github_url: formData.githubUrl,
      web_url: formData.liveUrl,
      description: formData.description,
    };

    setIsInternshipLoading(true);

    try {
      const response = await axios.post(
        STUDENT_INTERNSHIP_API.SUBMIT_INTERNSHIP,
        internshipPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        await fetchInternshipData();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error submiting internship:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setIsInternshipLoading(false);
    }
  };

  const handleViewCertificate = () => {
    if (!formData.certificate) return;
    setShowCertificateModal(true);
  };

  const handleDownloadCertificate = async () => {
    if (!formData.certificate) return;

    try {
      const response = await fetch(formData.certificate);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const studentName = formData.fullName.replace(/\s+/g, "_");
      link.href = url;
      link.download = `${studentName}_Internship_Certificate.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showErrorToast("Failed to download certificate");
    }
  };

  if (loadingInternship) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="student-certificate-section-container">
      {/* HEADER */}
      <Row className="mb-3">
        <h2 className="student-certificate-section-title">My Internship</h2>
        <p className="student-certificate-section-description">
          Gain real-world experience through hands-on internships.
        </p>
      </Row>

      {status === null && (
        <Form
          className="student-internship-form"
          onSubmit={handleInternshipSubmit}
        >
          <Row className="g-4 mb-3">
            <Col md={4}>
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                className={`student-internship-input ${
                  formData.fullName ? "filled" : ""
                } ${true ? "student-internship-readonly-field" : ""}`}
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                readOnly={true}
              />
            </Col>

            <Col md={4}>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                className={`student-internship-input ${
                  formData.email ? "filled" : ""
                } ${true ? "student-internship-readonly-field" : ""}`}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                readOnly={true}
              />
            </Col>

            <Col md={4}>
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                className={`student-internship-input ${
                  formData.phoneNumber ? "filled" : ""
                } ${true ? "student-internship-readonly-field" : ""}`}
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
                readOnly={true}
              />
            </Col>
          </Row>

          <Row className="g-4 mb-3 align-items-end">
            <Col md={4}>
              <Form.Label>Project Name</Form.Label>
              <Form.Control
                type="text"
                className={`student-internship-input ${
                  formData.projectName ? "filled" : ""
                }`}
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                placeholder="Enter your project name"
              />
            </Col>

            <Col md={4}>
              <Form.Label>GitHub URL</Form.Label>
              <Form.Control
                type="url"
                className={`student-internship-input ${
                  formData.githubUrl ? "filled" : ""
                }`}
                name="githubUrl"
                value={formData.githubUrl}
                onChange={handleInputChange}
                placeholder="e.g., https://github.com/username/project"
              />
            </Col>

            <Col md={4}>
              <Form.Label>Live Project URL</Form.Label>
              <Form.Control
                type="url"
                className={`student-internship-input ${
                  formData.liveUrl ? "filled" : ""
                }`}
                name="liveUrl"
                value={formData.liveUrl}
                onChange={handleInputChange}
                placeholder="e.g., https://myproject.vercel.app"
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                className={`student-internship-input ${
                  formData.description ? "filled" : ""
                }`}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Write a brief description about your project"
              />
            </Col>
          </Row>

          <Row>
            <Col className="text-end">
              <Button
                type="submit"
                disabled={isInternshipLoading}
                className="student-apply-internship-btn"
              >
                {isInternshipLoading ? "Submitting..." : "Apply Now"}
              </Button>
            </Col>
          </Row>

          <div className="student-internship-instruction-card mt-4 border-top">
            <p className="student-internship-instruction-title">
              Instruction & Details
            </p>
            <p>
              Skilledition, a product of Digisphere, provides career-focused
              internship programs designed to give students real industry
              exposure. Build practical skills, work on live projects, and
              enhance your confidence through hands-on learning experiences with
              expert guidance. Gain valuable experience, improve your
              professional skills, and prepare yourself for exciting career
              opportunities.
            </p>
          </div>
        </Form>
      )}

      {/*  DATA VIEW */}
      {status?.toLowerCase() === "pending" && (
        <Row className="justify-content-center">
          <Col lg={12}>
            <div className="student-internship-innerpage-wrapper">
              <img
                src={InnerPage}
                alt="Your profile searching"
                className="student-internship-innerpage-image"
              />
              <h4 className="student-internship-innerpage-title">
                Your application is currently under review by our team.
              </h4>
              <p className="student-internship-innerpage-description">
                We're carefully evaluating your profile and learning progress to
                find the best internship fit. Thank you for your patience we'll
                update you soon.
              </p>
            </div>
          </Col>
        </Row>
      )}

      {/* DUMMY DATA VIEW */}
      {["approved", "completed"].includes(status?.toLowerCase()) && (
        <div className="admin-internshipsss-management-table-wrapper">
          <Table className="admin-internshipsss-management-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Email</th>
                <th>Phone No</th>
                <th>Role</th>
                <th>
                  <div className="student-dashboard-sort-header">
                    Applied Date
                  </div>
                </th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Static Row 1 */}
              <tr>
                <td title={formData.fullName}>{formData.fullName}</td>
                <td title={formData.email}>{formData.email || "-"}</td>
                <td title={formData.phoneNumber}>
                  {formData.phoneNumber || "-"}
                </td>
                <td title={formData.role}>{formData.role || "-"}</td>
                <td>{formData.appliedDate ? formData.appliedDate : "-"}</td>
                <td>{formData.startDate ? formData.startDate : "-"}</td>
                <td>{formData.endDate ? formData.endDate : "-"}</td>
                <td title={status}>
                  <span
                    className={`admin-all-tutor-status ${status?.toLowerCase()}`}
                  >
                    {status || "-"}
                  </span>
                </td>
                <td>
                  <button
                    className="student-internshipsss-management-table-view-btns"
                    disabled={!formData.certificate}
                    onClick={handleViewCertificate}
                    title={
                      !formData.certificate
                        ? "Certificate not available yet"
                        : "View Certificate"
                    }
                    style={{
                      cursor: formData.certificate ? "pointer" : "not-allowed",
                      opacity: formData.certificate ? 1 : 0.5,
                    }}
                  >
                    <img
                      src={view}
                      alt="View"
                      className="student-internship-view-icon"
                    />
                  </button>

                  <button
                    className="admin-internshipsss-management-table-download-btn"
                    disabled={!formData.certificate}
                    onClick={handleDownloadCertificate}
                    title={
                      !formData.certificate
                        ? "Certificate not available yet"
                        : "Download Certificate"
                    }
                    style={{
                      cursor: formData.certificate ? "pointer" : "not-allowed",
                      opacity: formData.certificate ? 1 : 0.5,
                    }}
                  >
                    <img
                      src={download}
                      alt="View"
                      className="student-internship-view-icon"
                    />
                  </button>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        show={showCertificateModal}
        onHide={() => setShowCertificateModal(false)}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
        className="student-internship-modal"
      >
        <Modal.Header closeButton className="student-internship-modal-header">
          <Modal.Title className="student-internship-modal-title">
            Internship Certificate
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {formData.certificate ? (
            <iframe
              src={formData.certificate}
              title="Certificate"
              style={{ width: "100%", height: "500px" }}
              allowFullScreen
            />
          ) : (
            <p>Certificate not available.</p>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default StudentInternship;
